import {
  convertAndDownloadMarkdownToDocx,
  convertMarkdownToDocx,
  type MarkdownToDocxOptions,
} from './markdown-to-docx';
import { type DownloadFormat, type DownloadResult } from '../types/downloads';
import { downloadBlob } from './blob-download';

declare global {
  interface Window {
    html2canvas?: unknown;
    jspdf?: { jsPDF?: new (...args: any[]) => any };
    jsPDF?: new (...args: any[]) => any;
  }
}

export type DownloadMethod =
  | 'docx-conversion'
  | 'download-url'
  | 'blob'
  | 'markdown-blob'
  | 'pdf-from-markdown';

export interface HandleClientDownloadOptions {
  format: DownloadFormat;
  defaultFilename?: string;
  docxOptions?: MarkdownToDocxOptions;
}

export interface HandleClientDownloadResult {
  success: boolean;
  method?: DownloadMethod;
  error?: string;
}

const FORMAT_EXTENSION: Record<DownloadFormat, string> = {
  docx: '.docx',
  md: '.md',
  pdf: '.pdf',
};

function deriveFilename(
  providedName: string | undefined,
  format: DownloadFormat,
): string {
  const extension = FORMAT_EXTENSION[format];
  if (!providedName || !providedName.trim()) {
    return `document${extension}`;
  }

  const trimmed = providedName.trim();
  const lowerName = trimmed.toLowerCase();

  if (format === 'docx' && lowerName.endsWith('.md')) {
    return trimmed.replace(/\.md$/i, extension);
  }

  if (lowerName.endsWith(extension)) {
    return trimmed;
  }

  return `${trimmed}${extension}`;
}

function openDownloadUrl(url: string): void {
  if (typeof window === 'undefined') {
    throw new Error('Cannot open download URL outside the browser');
  }

  window.open(url, '_blank');
}

const scriptLoadCache = new Map<string, Promise<void>>();

async function loadExternalScript(src: string): Promise<void> {
  if (scriptLoadCache.has(src)) {
    return scriptLoadCache.get(src)!;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`Failed to load external script: ${src}`));
    document.head.appendChild(script);
  }).catch((error) => {
    scriptLoadCache.delete(src);
    throw error;
  });

  scriptLoadCache.set(src, promise);
  await promise;
}

// TODO: untested, just an idea for now
async function convertMarkdownToPdf(
  markdown: string,
  options: MarkdownToDocxOptions = {},
): Promise<Blob> {
  if (typeof window === 'undefined') {
    throw new Error('PDF conversion must run in the browser');
  }

  const docxBlob = await convertMarkdownToDocx(markdown, options);
  const arrayBuffer = await docxBlob.arrayBuffer();

  const mammoth = await import('mammoth/mammoth.browser');
  const { convertToHtml, images } = mammoth;

  const htmlResult = await convertToHtml({ arrayBuffer }, {
    convertImage: images.inline((element: any) =>
      element.read('base64').then((imageBuffer: string) => ({
        src: `data:${element.contentType};base64,${imageBuffer}`,
      })),
    ),
  });

  const html = htmlResult.value as string;

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = '794px';
  container.style.padding = '24px';
  container.style.backgroundColor = '#ffffff';
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    await loadExternalScript(
      'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js',
    );
    await loadExternalScript(
      'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js',
    );

    const globalWindow = window as typeof window & {
      html2canvas?: unknown;
      jspdf?: { jsPDF?: new (...args: any[]) => any };
      jsPDF?: new (...args: any[]) => any;
    };

    const JsPdfConstructor =
      globalWindow.jspdf?.jsPDF || globalWindow.jsPDF;
    if (!JsPdfConstructor) {
      throw new Error('jsPDF library failed to load.');
    }

    const pdf = new JsPdfConstructor({ unit: 'pt', format: 'a4' });
    await pdf.html(container, {
      margin: [30, 30, 30, 30],
      autoPaging: 'text',
      html2canvas: {
        scale: 0.8,
        useCORS: true,
        logging: false,
        allowTaint: true,
      },
    });

    const pdfBlob = pdf.output('blob');
    if (pdfBlob instanceof Blob) {
      return pdfBlob;
    }

    return new Blob([pdfBlob], { type: 'application/pdf' });
  } finally {
    document.body.removeChild(container);
  }
}

export async function handleClientDownload(
  result: DownloadResult,
  options: HandleClientDownloadOptions,
): Promise<HandleClientDownloadResult> {
  if (typeof window === 'undefined') {
    return {
      success: false,
      error: 'Client downloads can only run in the browser.',
    };
  }

  if (!result.success) {
    return {
      success: false,
      error: result.error || 'Download request failed.',
    };
  }

  const filename = deriveFilename(
    result.filename || options.defaultFilename,
    options.format,
  );

  try {
    switch (options.format) {
      case 'docx': {
        if (result.markdownContent) {
          await convertAndDownloadMarkdownToDocx(
            result.markdownContent,
            filename,
            options.docxOptions,
          );
          return { success: true, method: 'docx-conversion' };
        }

        if (result.blob) {
          downloadBlob(result.blob, filename);
          return { success: true, method: 'blob' };
        }

        if (result.downloadUrl) {
          openDownloadUrl(result.downloadUrl);
          return { success: true, method: 'download-url' };
        }

        break;
      }
      case 'md': {
        if (result.markdownContent) {
          const blob = new Blob([result.markdownContent], {
            type: 'text/markdown',
          });
          downloadBlob(blob, filename);
          return { success: true, method: 'markdown-blob' };
        }

        if (result.blob) {
          downloadBlob(result.blob, filename);
          return { success: true, method: 'blob' };
        }

        if (result.downloadUrl) {
          openDownloadUrl(result.downloadUrl);
          return { success: true, method: 'download-url' };
        }

        return {
          success: false,
          error: 'No markdown content available for download.',
        };
      }
      case 'pdf': {
        if (result.markdownContent) {
          const pdfBlob = await convertMarkdownToPdf(
            result.markdownContent,
            options.docxOptions,
          );
          downloadBlob(pdfBlob, filename);
          return { success: true, method: 'pdf-from-markdown' };
        }

        if (result.blob) {
          downloadBlob(result.blob, filename);
          return { success: true, method: 'blob' };
        }

        if (result.downloadUrl) {
          openDownloadUrl(result.downloadUrl);
          return { success: true, method: 'download-url' };
        }

        return {
          success: false,
          error: 'No content available to generate PDF.',
        };
      }
      default:
        return {
          success: false,
          error: `Unsupported download format: ${options.format}`,
        };
    }
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Unknown error during download handling.',
    };
  }

  return {
    success: false,
    error: 'No download payload returned from the server.',
  };
}
